<template>
    <IonPage>
        <IonContent data-cy="content-loading">
            <div class="py-20 px-12 flex items-center space-x-2 w-full h-full">
                <div
                    v-if="error"
                    class="w-full"
                >
                    <Empty icon="error">
                        <template #icon>
                            <svg
                                width="40"
                                height="40"
                                viewBox="0 0 40 40"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clip-path="url(#clip0_911_13591)">
                                    <path
                                        d="M38.271 36.1107L20.0563 36.1365C20.0219 35.9902 19.9961 35.8353 19.9961 35.6805V3.88867L38.271 36.1107Z"
                                        fill="#F4F5F7"
                                    />
                                    <path
                                        d="M39.7675 35.2588L21.4668 2.99382L21.3635 2.83895C21.0108 2.38294 20.4515 2.12482 19.875 2.17644C19.2986 2.21086 18.7823 2.53781 18.4984 3.04545L0.18908 35.3191L0.120248 35.4739C-0.0948524 36.0074 -0.0260204 36.601 0.292328 37.0743C0.61928 37.5475 1.14412 37.8314 1.72059 37.8314H38.2704C38.8899 37.8314 39.4491 37.5131 39.7589 36.9796C40.0772 36.4376 40.0772 35.8009 39.7589 35.2588H39.7675ZM1.69478 36.1622L19.9955 3.88864L38.2704 36.1106L1.69478 36.1622Z"
                                        fill="#6B7280"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M20.0041 15.1599C20.4773 15.1599 20.8645 15.5471 20.8645 16.0203V26.3451C20.8645 26.8183 20.4773 27.2055 20.0041 27.2055C19.5309 27.2055 19.1437 26.8183 19.1437 26.3451V16.0203C19.1437 15.5471 19.5309 15.1599 20.0041 15.1599Z"
                                        fill="#6B7280"
                                    />
                                    <path
                                        d="M20.0041 31.5075C20.4773 31.5075 20.8645 31.1203 20.8645 30.6471C20.8645 30.1739 20.4773 29.7867 20.0041 29.7867C19.5309 29.7867 19.1437 30.1739 19.1437 30.6471C19.1437 31.1203 19.5309 31.5075 20.0041 31.5075Z"
                                        fill="#6B7280"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_911_13591">
                                        <rect
                                            width="40"
                                            height="35.6636"
                                            fill="white"
                                            transform="translate(0 2.16797)"
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                        </template>

                        <template #title>
                            Oops, something went wrong.
                        </template>

                        <template #subtitle>
                            <template v-if="appNotFound">
                                <p>Unfortunately, this app is no longer available or operational. We apologize for any inconvenience this may cause.</p>
                                <p class="mt-4">
                                    For more information, please contact your school.
                                </p>
                            </template>
                            <template v-else>
                                Please check your internet connection, or try again soon.
                            </template>
                        </template>
                    </Empty>

                    <div
                        v-if="showButtons"
                        class="flex flex-col items-center -mt-10 mb-6"
                    >
                        <DButton
                            theme="base"
                            @click="retry"
                        >
                            Try again
                        </DButton>
                    </div>
                </div>
                <div
                    v-else-if="loading"
                    class="absolute inset-0 flex items-center justify-center"
                    data-cy="launch-loading"
                >
                    <div class="flex flex-col items-center justify-center">
                        <UILoading class="w-20 h-20 fill-blue-700! text-blue-700/30!" />
                        <div
                            v-if="status"
                            class="h-0"
                        >
                            <div class="mt-2 text-gray-300 text-sm">
                                {{ status }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="error && !appNotFound"
                class="px-10 absolute bottom-0 inset-x-0 text-sm text-gray-600 p-2 text-center mb-(--beyond-safe-area-inset-bottom)"
            >
                <div class="mb-6">
                    Still having issues? You may need to reset the app to its default settings.
                </div>
                <div class="mb-6">
                    <DButton
                        link-color="base"
                        theme="text-link"
                        class="mx-auto"
                        @click="reset"
                    >
                        Reset app
                    </DButton>
                </div>
                <div class="text-xs">
                    {{ error }}
                </div>
            </div>
        </IonContent>
    </IonPage>
</template>

<script setup>
    import semver from 'semver'
    import { isAxiosError } from 'axios'
    import { useRouter } from 'vue-router'

    import { DButton, Colors, preloadIcons } from '@digistorm/spark'
    import { Capacitor } from '@capacitor/core'
    import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update'
    import { App } from '@capacitor/app'
    import { Dialog } from '@capacitor/dialog'
    import { CapacitorUpdater } from '@capgo/capacitor-updater'
    import * as SentryVue from '@sentry/vue'

    import {
        api, analytics, preferences, mock,
    } from '@/services'
    import { AppIdentity } from '@/plugins/app-identity'
    import { biometric } from '@/services/biometric'
    import { addCypressWindowListener } from '@/composables/cypress'
    import { useLaunchHelpers } from '@/composables/launchHelpers'
    import { useLoadingOverlays } from '@/composables/loadingOverlays'
    import versionTypes from '@/constants/versionTypes'
    import { useWidgetsStore } from '@/stores/widgets'
    import { usePlatformAuthStore } from '@/stores/platformAuth'
    import { usePlatformAuthUrlStore } from '@/stores/platformAuthUrl'

    const appStore = useAppStore()
    const authStore = useAuthStore()
    const platformAuthStore = usePlatformAuthStore()
    const platformAuthUrlStore = usePlatformAuthUrlStore()
    const deviceStore = useDeviceStore()
    const moduleStore = useModuleStore()
    const moreStore = useMoreStore()
    const notificationsStore = useNotificationsStore()
    const versionStore = useVersionStore()
    const identityStore = useIdentityStore()

    const authHelpers = useAuthHelpers()
    const moduleHelpers = useModuleHelpers()
    const pushNotifications = usePushNotifications()
    const launchHelpers = useLaunchHelpers()
    const { snackbar } = useSnackbars()
    const loadingOverlays = useLoadingOverlays()

    const router = useRouter()
    const { appVersion } = storeToRefs(appStore)
    const { version } = storeToRefs(versionStore)

    const status = ref(null)
    const error = ref(null)
    const showButtons = ref(true)
    const loading = ref(true)
    const biometricListener = ref(null)
    const checkedStoreAt = ref(null)
    const hotCodeUpdating = ref(false)
    const restarting = ref(false)
    const appNotFound = ref(false)

    let newAppVersion = {}

    const hotReloadDisabled = import.meta.env.VITE_HOT_RELOAD_DISABLED

    const doesHotCodeUpdates = computed(() => {
        return (Capacitor.isNativePlatform() || window.Cypress)
            && (!hotReloadDisabled || hotReloadDisabled === 'false')
    })

    const checkLegacyStorage = async () => {
        // Skip if already migrated
        if (await preferences.getGlobalItem('legacy_migrated') === 'true') {
            return
        }

        // Get device ids from legacy storage
        // Note: The legacy app did not store the reach device id
        await preferences.useLegacyStorage(true)

        const payload = {}
        try {
            const notificationManagerDevice = await preferences.getGlobalItem('school-app-notificationManagerDevice')
            payload.notification_manager_id = JSON.parse(notificationManagerDevice)?.id
        } catch (e) {
            console.error('Error parsing notificationManagerDevice', e)
        }
        try {
            const appControlDevice = await preferences.getGlobalItem('school-app-app-control')
            payload.app_control_id = JSON.parse(appControlDevice)?.device_id
        } catch (e) {
            console.error('Error parsing appControlDevice', e)
        }

        await preferences.useLegacyStorage(false)

        // Send the data to api
        try {
            await authStore.migrate(payload)

            // Set the flag to prevent re-migration
            await preferences.setGlobalItem('legacy_migrated', 'true')
        } catch (e) {
            console.error('Error migrating legacy storage', e)
        }
    }

    const checkBiometry = async () => {
        if (!version.value.biometrics_enabled) {
            return
        }

        const biometryEnabled = await biometric.isEnabled()
        console.log('Biometrics enabled is:', biometryEnabled)
        if (biometryEnabled) {
            try {
                await biometric.authenticate()

                // success
            } catch (error) {
                // something went wrong with biometrics
                throw new Error('Issue authenticating biometrics')
            }
        }
    }

    const viewNotification = (notification, replace = false) => {
        // TODO: Consider appending a placeholder / loading module while the notification content is loading instead of this overlay
        // TODO: This would the user to see something is happening while still being able to cancel it by hitting the back button
        loadingOverlays.push()

        authStore.pushFormat(notification)
            .then((link) => {
                loadingOverlays.pop()

                moduleHelpers.openModuleOrOpenBrowserForLinkData(router, link, replace)
                notificationsStore.fetch(true)
            })
            .catch((e) => {
                loadingOverlays.pop()

                if (authHelpers.isAuthenticationException(e)) {
                    authHelpers.handleAuthenticationException(
                        e.response.data,
                        router,
                        (payload) => {
                            // Try again now that we're logged in, replace the view this time as the login view is still loaded
                            viewNotification(notification, true)
                        },
                        () => {
                            // Navigate backwards
                            router.back()
                        },
                        true,
                        true,
                    )

                    // Prevent error from appearing while the login view launches
                    return
                }
                console.error('error', e)
                snackbar({
                    message: 'Error loading notification details',
                    type: 'error',
                })
            })
    }

    const handleNotification = (notification, prompt = false) => {
        notificationsStore.removeAllDeliveredNotifications()
            .catch((error) => {
                console.log('Unable to clear notifications', error)
            })

        if (!prompt) {
            viewNotification(notification)
            return
        }

        Dialog.confirm({
            title: notification.title,
            message: notification.body,
            okButtonTitle: 'View',
            cancelButtonTitle: 'Dismiss',
        })
            .then(({ value }) => {
                if (value) {
                    viewNotification(notification)
                }
            })
    }

    const addMockDataListeners = () => {
        appStore.events.on('mockDataEnabled', () => {
            if (version.value.type === versionTypes.nextGen) {
                const spacesStore = useSpacesStore()
                const widgetsStore = useWidgetsStore()

                spacesStore.clear()
                widgetsStore.clearAllDependencies()
                spacesStore.fetch()
            } else {
                const badgeStore = useBadgeStore()
                const dashboardStore = useDashboardStore()

                badgeStore.refreshAuthFailures()
                dashboardStore.load()
            }

            moreStore.load()
            identityStore.load()
            moduleHelpers.navigateBackToRoot(router)
        })
    }

    const addPushListeners = () => {
        pushNotifications.events.on('pushNotificationActionPerformed', (action) => {
            handleNotification(action.notification, false)
        })

        pushNotifications.events.on('pushNotificationReceived', (notification) => {
            handleNotification(notification, true)
        })

        // Add native event listeners AFTER our event listeners are registered
        // The native event listeners will be triggered immediately by capacitor when registered,
        // so we need to ensure our listeners are added first
        pushNotifications.addListeners()

        // If push notifications are enabled we should always call register on startup
        // This is required as the push token can change over time and must be synced to API
        pushNotifications.initTokenSync()
    }

    const loadApp = () => {
        CapacitorUpdater.notifyAppReady()

        appStore.markLaunched()

        SentryVue.setTag('app_id', appStore.id)
        SentryVue.setTag('js_version', appStore.appVersion)
        SentryVue.setTag('native_version', appStore.nativeIdentity?.appVersion)
        SentryVue.setTag('device_external_id', deviceStore.device.identifier)

        analytics.setUserProperty({
            name: 'appName',
            value: appStore.info.name,
        })
        analytics.setUserProperty({
            name: 'appId',
            value: appStore.info.id,
        })
        analytics.setUserProperty({
            name: 'appVersion',
            value: appStore.info.version,
        })

        if (!mock.mocking()) {
            api.get(`/${appStore.id}/devices/context`)
                .then((response) => {
                    authHelpers.handleUserContextChange(response.data)
                })
                .catch((e) => {
                    console.error('Error fetching user context', e)
                })
        }

        addMockDataListeners()

        // Load the mock module if there is one
        if (mock.mockingModule()) {
            moduleStore.addModule({
                id: 'mock-module',
                data: mock.content(),
                link: {
                    type: 'api-component',
                    url: '/00000000-0000-0000-0000-000000000000/modules/00000000-0000-0000-0000-000000000000',
                },
            })
            router.push({ name: 'view', params: { id: 'mock-module' } })
            return
        }

        return launchHelpers.loadHomepage(router, true)
            .then(() => {
                if (version.value.notifications_enabled) {
                    // Add push listeners once the dashboard is loaded to ensure app updates have been performed
                    // before showing any push notification popups
                    addPushListeners()
                }
            })
    }

    const isNewerAppVersion = (version) => {
        return semver.valid(version)
            && semver.valid(appVersion.value)
            && semver.gt(version, appVersion.value)
    }

    const downloadLatestAppVersion = async () => {
        if (!doesHotCodeUpdates.value) {
            return
        }
        console.log('Checking for new version')
        const data = await appStore.checkVersion()

        if (isNewerAppVersion(data.version)) {
            console.log('Downloading new version', data.version)
            status.value = 'Updating app...'

            hotCodeUpdating.value = true
            try {
                const version = await CapacitorUpdater.download(data)
                restarting.value = true
                status.value = 'Restarting app...'

                await CapacitorUpdater.set(version)
                // Throw an exception to prevent further execution of the app launch process
                // As the app is restarting it can lead to unexpected behaviour if this happens halfway through a process e.g. migration
                // Once the app has restarted the launch process will continue as expected
                throw new Error('App is restarting to install update')
            } catch (e) {
                console.error('Update failed:', e)
                SentryVue.captureException(e)
                throw new Error('We couldn\'t download or set the new app version.')
            } finally {
                hotCodeUpdating.value = false
            }
        }

        status.value = null
    }

    const checkAppVersion = async () => {
        // Skip if we're already downloading an update
        if (!doesHotCodeUpdates.value || hotCodeUpdating.value) {
            return
        }
        console.log('Check app version', hotReloadDisabled)
        const data = await appStore.checkVersion()
        if (isNewerAppVersion(data.version)) {
            hotCodeUpdating.value = true
            try {
                newAppVersion = await CapacitorUpdater.download(data)
            } finally {
                hotCodeUpdating.value = false
            }
        }
    }

    const checkStoreVersion = () => {
        if (!Capacitor.isNativePlatform()) {
            return
        }
        if (checkedStoreAt.value && (checkedStoreAt.value - Date.now()) < 300000) {
            // Don't check for updates if already checked within 5 minutes
            return
        }
        checkedStoreAt.value = Date.now()
        AppUpdate.getAppUpdateInfo()
            .then((result) => {
                if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
                    return
                }
                if (deviceStore.device.platform === 'android') {
                    AppUpdate.startFlexibleUpdate()
                } else {
                    Dialog.confirm({
                        title: 'Update available',
                        message: 'To use this app, download the latest version.',
                        okButtonTitle: 'Update',
                        cancelButtonTitle: 'Dismiss',
                    })
                        .then(({ value }) => {
                            if (!value) {
                                return
                            }
                            AppUpdate.openAppStore()
                        })
                }
            })
    }

    const retry = () => {
        appStore.restart()
    }

    const reset = () => {
        api.post(`/${appStore.id}/settings/reset`)
            .finally(() => {
                appStore.resetStorage()
                appStore.restart()
            })
    }

    const handleBrowserUrlAuthCompletion = (url) => {
        if (!url) {
            return Promise.resolve()
        }

        // Display error messages from the auth store in App Viewer
        platformAuthStore.events.on('error', (e) => {
            if (moduleHelpers.isActionException(e)) {
                moduleHelpers.handleActionException(router, e.response.data)
            }
        })

        platformAuthStore.events.on('login', async (payload) => {
            // Set detail in local storage
            await preferences.setGlobalItem('url_auth_completed_event', JSON.stringify(payload))

            // Close this window
            try {
                window.close()
            } catch (e) {
                console.log('Error closing window', e)
            }
        })

        return platformAuthUrlStore.complete({ url })
            .then(() => {
                showButtons.value = false

                throw new Error('Please close this window and continue on the original app window.')
            })
    }

    const handleAppUrlOpen = (event) => {
        // Fetch URL after the scheme
        const url = event.url.split('//')[1]
        if (!url) {
            return
        }
        // Handle authentication complete`
        if (startsWith(url, 'auth/complete')) {
            // Fetch the complete URL from the query parameter
            const params = new URLSearchParams(url.split('?')[1])
            const completeUrl = params.get('complete_url')

            // Complete the authentication flow by requesting the complete URL
            platformAuthUrlStore.complete({ url: completeUrl })
        }
        // Handle draft mode links
        if (startsWith(url, 'version/')) {
            const versionId = url.split('/')[1]
            appStore.setDraftVersion(versionId)
                .then(() => {
                    appStore.restart()
                })
        }
    }

    // Ensure listeners aren't added before app info has loaded
    // Events like appUrlOpen are retained by capacitor until the listener is registered, we don't
    // need to worry about when it's registered
    const addListeners = () => {
        App.addListener('appUrlOpen', handleAppUrlOpen)
        addCypressWindowListener('App', 'appUrlOpen', handleAppUrlOpen)

        App.addListener('resume', (event) => {
            console.log('App resumed')

            notificationsStore.fetch()

            if (!Capacitor.isNativePlatform() || !authStore.registered) {
                return
            }

            // Trigger an update of device details
            authStore.update()
        })

        App.addListener('appStateChange', async (state) => {
            console.log('App state changed', state)

            if (state.isActive) {
                checkAppVersion()
                checkStoreVersion()
            }

            if (!state.isActive && isNewerAppVersion(newAppVersion.version) && !hotCodeUpdating.value) {
                hotCodeUpdating.value = true
                CapacitorUpdater.next(newAppVersion)
                    .catch((e) => {
                        console.error('Error setting new app version', e)
                    })
                    .finally(() => {
                        // Clear the new app version so we don't try to install it again
                        newAppVersion = {}
                        hotCodeUpdating.value = false
                    })
            }
        })

        window.addEventListener('popstate', (e) => {
            // Modals push an empty state
            if (!e.state) {
                return
            }
            // Remove all modules that were on top of the new module
            const module = /^\/view\/([^/]+)/.exec(e.state.current)?.[1]
            if (module) {
                moduleStore.removeModulesUntil(module)
            } else {
                moduleStore.removeAllModules()
            }
        })
    }

    const setApiUrl = async (regionOverride) => {
        const url = Capacitor.isNativePlatform()
            ? await AppIdentity.getEnvironment().then((environment) => environment.apiUrl)
            : import.meta.env.VITE_API_URL

        if (!regionOverride) {
            api.defaults.baseURL = url
            return
        }

        // Sanitise the regionOverride, only support a-z 0-9 .
        const sanitisedRegion = replace(regionOverride, /[^a-z0-9.]/gi, '')

        // Replace the region prefix in the API_URL
        api.defaults.baseURL = replace(import.meta.env.VITE_API_URL, /(?<=\/\/)[a-z0-9]+(?=\.api)/i, sanitisedRegion)
    }

    onBeforeMount(async () => {
        await biometric.updateBiometryInfo()

        try {
            biometricListener.value = biometric.addResumeListener()
        } catch (error) {
            if (error instanceof Error) {
                console.error(error.message)
            }
        }
    })

    onMounted(() => {
        const initialSearchParams = new URLSearchParams(appStore.initialUrlSearch)

        // Check if the app was started with an app ID specified in the URL
        const appIdOverride = initialSearchParams.has('app_id')
            ? initialSearchParams.get('app_id')
            : null

        // Check if the app was started with a region specified in the URL
        const regionOverride = initialSearchParams.has('region')
            ? initialSearchParams.get('region')
            : null

        // Check if the app should use mock data
        const mockData = initialSearchParams.has('mock_data') || import.meta.env.VITE_MOCK_DATA

        // Load device before doing anything else
        appStore.init(appIdOverride, mockData, initialSearchParams.get('version'))
            .then(() => setApiUrl(regionOverride))
            .then(() => Promise.all([
                authStore.init(),
                deviceStore.init(),
            ]))
            .then(() => console.log('Auth + device stores initialised'))
            .then(() => {
                // If we have already registered trigger an update, but don't wait for it complete
                if (authStore.registered) {
                    authStore.update()
                    return Promise.resolve()
                }

                return authStore.register()
            })
            .then(async () => {
                await versionStore.load()

                console.log('Version', toRaw(version.value))

                if (version.value.primary_color) {
                    const generatedColors = Colors.generateColorMap(version.value.primary_color)

                    // generate css
                    const generatedStyles = join(map(generatedColors, (value, key) => {
                        const rgb = split(value, ' ')
                        const color = Colors.rgbToHex(rgb[0], rgb[1], rgb[2])

                        // create a set of --color-brand-opacity variable based on color-700
                        if (key === '700') {
                            return `--color-brand-${ key }: ${ color };
                            --color-brand-opacity-5: ${ color }0d;
                            --color-brand-opacity-10: ${ color }1a;
                            --color-brand-opacity-12: ${ color }1f;
                            --color-brand-opacity-20: ${ color }33;
                            --color-brand-opacity-30: ${ color }4d;
                            --color-brand-opacity-40: ${ color }66;
                            --color-brand-opacity-50: ${ color }80;
                            --color-brand-opacity-60: ${ color }99;
                            --color-brand-opacity-70: ${ color }b2;
                            --color-brand-opacity-80: ${ color }cc;
                            --color-brand-opacity-90: ${ color }e5;`
                        }

                        return `--color-brand-${ key }: ${ color };`
                    }), '\n')

                    // add a style tag with generated css
                    const currentStyles = document.getElementById('digistorm-ui-primary-color')
                    if (currentStyles) {
                        currentStyles.remove()
                    }
                    const style = document.createElement('style')
                    style.setAttribute('id', 'digistorm-ui-primary-color')
                    style.innerHTML = `:root { ${generatedStyles} }`
                    document.getElementsByTagName('head')[0].appendChild(style)
                }

                notificationsStore.setNotificationsEnabled(version.value.notifications_enabled)
            })
            .catch((e) => {
                // Catch register error for new users or version load error for existing users
                if (isAxiosError(e) && e.response?.status === 404) {
                    appNotFound.value = true
                }
                throw e
            })
            .then(() => checkBiometry())
            .then(() => addListeners())
            .then(async () => {
                // If there's an error downloading the latest version, stop launch
                await downloadLatestAppVersion()
            })
            .then(() => checkStoreVersion())
            .then(() => handleBrowserUrlAuthCompletion(initialSearchParams.get('url_auth_complete_url')))
            .then(() => checkLegacyStorage())
            .then(() => {
                // Initiate preloading of icons without blocking app launch
                preloadIcons([
                    'heroicons:calendar-solid',
                    'heroicons:calendar',
                    'heroicons:chevron-down-20-solid',
                    'heroicons:chevron-left-20-solid',
                    'heroicons:chevron-right-20-solid',
                    'heroicons:chat-bubble-bottom-center-solid',
                    'heroicons:chat-bubble-bottom-center',
                    'heroicons:home-solid',
                    'heroicons:home',
                    'heroicons:identification-solid',
                    'heroicons:identification',
                    'heroicons:newspaper-solid',
                    'heroicons:newspaper',
                    'heroicons:x-mark-20-solid',
                    'ph:arrow-light',
                    'ph:arrow-square-out-light',
                    'ph:bell',
                    'ph:bell-fill',
                    'ph:calendar',
                    'ph:calendar-blank-fill',
                    'ph:calendar-blank',
                    'ph:calendar-dot',
                    'ph:calendar-dots',
                    'ph:caret-left',
                    'ph:caret-left-light',
                    'ph:caret-right',
                    'ph:caret-right-light',
                    'ph:chats-circle-fill',
                    'ph:chats-circle',
                    'ph:circles-four-fill',
                    'ph:circles-four',
                    'ph:funnel-light',
                    'ph:gear-light',
                    'ph:house-fill',
                    'ph:house',
                    'ph:info-light',
                    'ph:list-bold',
                    'ph:list',
                    'ph:newspaper-fill',
                    'ph:newspaper',
                    'ph:rows',
                    'ph:sign-out-light',
                    'ph:sliders-horizontal',
                ]).catch((e) => console.log('Error preloading icons', e))
            })
            .then(() => loadApp())
            .catch((e) => {
                console.log('Error launching app', e)
                error.value = e
            })
    })

    onBeforeUnmount(async () => {
        (await biometricListener.value)?.remove()
        loading.value = false
    })
</script>
